import React from 'react'
import Game from './Game'
import { render, getConfig, shuffle, CONTENT_TYPE } from '@educaplay/core'
import { assetsUrl, GameProvider } from '@educaplay/core'
import { initialize } from '@educaplay/store'
import { formatText, generateDropdownOptions } from './utils/functions'
import FibConfigProvider from './components/FibConfigProvider'
import classes from './Main.module.scss'
import clsx from 'clsx'
import { Wrong } from '@educaplay/core/Icons/Wrong'
import { Check } from '@educaplay/core/Icons/Check'
import CorrectionResults from './components/CorrectionResults'
import { initErrorMonitoring } from  '@educaplay/core/utils';
import { createCustomization } from '@educaplay/core/utils/customization'

initErrorMonitoring("https://96efb84487c06902a3600b7c1442ddb2@o4509038211104768.ingest.de.sentry.io/4509050692108368", {
  tracesSampleRate: 0.05
});

const gameImage = assetsUrl("COMPLETAR/logoActivity.png");
const container = document.getElementById('root');

const COMPLETAR_SOUNDS = {
  'completar-submit': assetsUrl('COMPLETAR/activar.mp3'),
  'completar-hoverButton': assetsUrl('COMPLETAR/bienactivo.mp3'),
  'completar-load': assetsUrl('common/carga.mp3'),
  'completar-loadBank': assetsUrl('common/enunciado.mp3'),
  'completar-click': assetsUrl('COMPLETAR/escalar.mp3'),
  'completar-active':assetsUrl('COMPLETAR/activo.mp3'),
  'completar-write': assetsUrl('COMPLETAR/escribir_low.mp3'),
  'completar-move': assetsUrl('COMPLETAR/mover.mp3'),
  'completar-hover': assetsUrl('COMPLETAR/hover.mp3'),
  'completar-correct': assetsUrl('common/bien.mp3'),
  'completar-wrong': assetsUrl('COMPLETAR/fallo.mp3'),
}

async function init() {
  const { data, texts, settings, user, title, description, author, id, url } = await getConfig().catch((err) => 
    console.error('Error al obtener la configuración: ', err)
  )

  const defaultQuestionType = data.FORMA_COMPLETAR || "click"

  const { resources } = settings
  const filteredQuestions = data.PREGUNTAS
  .filter((question) => question.TEXTO!== "")
  .filter((question) => question.PALABRAS.length > 0)
  .filter((question) => question.FORMA_COMPLETAR !== "dropdown" || Object.keys(question.DROPDOWNS ?? {}).length > 0)
  
  // const { resources } = settings
  
  const questionTimeNumber = Number(data.TIEMPO)
  const questionTime = Number.isNaN(questionTimeNumber) ? 0 : Math.max(questionTimeNumber, 10) //Avoid a value of 0, it causes an error on the store

  const keySensitive = {
    caps: data.SENSIBLE_MAYUSCULAS === "si",
    accent: data.SENSIBLE_ACENTOS === "si"
  };
  
  const questions = filteredQuestions.map((question, index) => {
    const filteredWords = question.PALABRAS
    .filter((word) => {
      if (question.FORMA_COMPLETAR !== "dropdown") {
        return true
      }
      if (question.UNIDAS.includes(word) || word in (question.DROPDOWNS ?? {})) {
        return true
      }
      return false
    });
    const formattedText = formatText({texto: question.TEXTO, palabras: filteredWords, unidas: question.UNIDAS})
    
    const words = formattedText.flat().filter(text => text.isGap).map(text => text.data);
    const finalWords = [...words, ...question.INCORRECTAS];
    const shuffledWords = shuffle(finalWords);

    const points = 100 / filteredQuestions.length;

    const calculatePoints = (answer) => {
      const pointsPerWord = points / words.length;
      const totalPoints = Object.values(answer).reduce((acc, block) => {
        if (!block.isCorrect) return acc;

        if (block.answeredBlanks.includes(block.id)) return acc;

        if (block.tries === 0) return acc + pointsPerWord;
        if (block.tries === 1) return acc + (pointsPerWord * 0.5);
        if (block.tries === 2) return acc + (pointsPerWord * 0.25);

        return acc
      }, 0);
      return Math.max(totalPoints, 0);
    }

    const dropdownOptions = generateDropdownOptions({arrayWithIds: formattedText, originalMap: question.DROPDOWNS ?? {}});

    return {
      text: question.TEXTO,
      bank: (question.ENUNCIADO || question.MULTIMEDIA) ? 
      {
        text: question.ENUNCIADO,
        source: question.MULTIMEDIA_TIPO === CONTENT_TYPE.YOUTUBE ? question.MULTIMEDIA : `${resources}${question.MULTIMEDIA}`,
        type: question.MULTIMEDIA_TIPO ?? CONTENT_TYPE.TEXT
      } : null,
      dropdownOptions,
      hideWordBank: data.AYUDA === "no",
      formattedText,
      shuffledWords,
      questionType: question.FORMA_COMPLETAR || defaultQuestionType,
      checkAnswer: (correction) => Object.values(correction).every(correction => correction.isCorrect),
      correctAnswers: question.RESPUESTAS,
      answer: null,
      points,
      calculatePoints,
      time: questionTime,
      id: index,
      fib_extraWords: question.INCORRECTAS 
    }
  });

  initialize({
    lives: data.NUMERO_INTENTOS, //COMPROBAR SI ES MAXIMO
    countFooterSpace: true,
    liveActived: data.NUMERO_INTENTOS_INFINITOS !== "si",
    hasCountDown: data.MAXIMO === "si",
    randomized: data.ALEATORIO === "si",
    showSolutions: data.OCULTAR_RESPUESTAS !== "1",
    showRestart: data.OCULTAR_REINICIAR !== "1",
    showSocialMedia: data.OCULTAR_REDES !== "1",
    finalText: data.TEXTO_FINAL,
    hasBank: questions.some(question => question.bank),
    hasQuestionBank: true,
    hasInfinityTries: true,
    semiCorrect: true,
    questions,
    settings,
    title,
    description,
    author,
    id,
    url,
    user,
    customizations: createCustomization(data)
  });

  render(container, (
    <React.StrictMode>
      <FibConfigProvider keySensitive={keySensitive} >
        <GameProvider
          gameImage={gameImage}
          texts={texts}
          sounds={COMPLETAR_SOUNDS}
          Game={Game}
          mappers={{
            report: (question, index) => {
              const blankIds = question.formattedText.flat().filter(text => text.isGap).map(text => text.id);

              const triesArray = question.tries.flatMap(item => Object.values(item)).map(entry => {
                if (entry.isCorrect === false) {
                  const blankId = blankIds.indexOf(entry.id);
                  return {
                    s: entry.isCorrect? 1 : 0,
                    i: `${index}:${blankId}`,
                    a: entry.userAnswer ?? ""
                  }
                }
                return null;
              }).filter(entry => entry !== null);
  
              const answer = Object.values(question.answer).map(entry => {
                const blankId = blankIds.indexOf(entry.id);
                return {
                  s: entry.isCorrect? 1 : 0,
                  i: `${index}:${blankId}`,
                  a: entry.userAnswer ?? ""
                }
              });
              return [...triesArray, ...answer];
            }
          }}
          renders={{
            results: (questions) => {
              return (
                <div className={classes.answerdisposition}>
                  {questions.map((question, index) => {
                    const hasBank = question.bank ?? false;
                    const isCorrect = question.tries.length <= 0;
                    return (
                      <div key={index} className={classes.answerblock} style={{ backgroundColor: isCorrect ? "#559107" : "#E08876" }}>
                        <div className={classes.headeranswer}>
                          <div >
                            <span className={clsx(classes.number, isCorrect && classes.correct)} style={{ color: isCorrect ? "#FFFFFF" : "#112000" }}>{(index + 1) + "."}</span>
                          </div>
                          <div>
                            {hasBank && question.bank.text}
                          </div>
                          <div className={classes.symbol}>
                            {isCorrect ? <Check  /> : <Wrong />}
                          </div>
                        </div>
                        <div className={clsx(classes.bodyanswer, isCorrect && classes.correct)} style={{ color: isCorrect ? "white" : "black" }}>
                          <CorrectionResults question={question} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            },
            infoLeft: (currentIndex, questions) => {
              return `${texts.specific.screen}: ${currentIndex + 1} / ${questions.length}`;
            },
          }}
        />
      </FibConfigProvider>
    </React.StrictMode>
  ))
}

init();